import { offset, shift } from "@floating-ui/dom";
import { useContext, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { Provider, useDispatch, useSelector } from "react-redux";
import Shepherd from "shepherd.js";
import OnBoarding from "../components/OnBoarding/OnBoarding";
import { isWidgetDisplayed } from "../esri/widgets/expandUtils";
import { setOnboardingActive } from "../redux/action/Onboarding-actions";
import store from "../redux/store";
import { ConfigContext } from "../utils/ConfigContext";
import { EMBED_VARIANTS, ROLE_EDITOR } from "../utils/helper";

const DESKTOP_STEPS = [
  {
    className: "onboarding-modules",
    position: "bottom",
    translation: "modules",
  },
  {
    className: "onboarding-report",
    position: window.isSmall ? "top" : "bottom",
    translation: "report",
    widget: "Report",
  },
  {
    className: "onboarding-latest-updates",
    position: "bottom",
    translation: "latestUpdates",
    widget: "LatestUpdates",
  },
  {
    className: "onboarding-insights",
    position: "bottom",
    translation: "insights",
    widget: "Insights",
  },
  {
    className: "onboarding-legend",
    widget: "SymbologyLegend",
    position: "top",
    translation: "legend",
  },
  {
    className: "onboarding-feature-table",
    widget: "FeatureTable",
    position: "top",
    translation: "featureTable",
  },
  {
    className: "onboarding-sidebar-expand",
    position: "right",
    translation: "sidebarExpand",
  },
  {
    className: "onboarding-search",
    widget: "Search",
    position: "left",
    translation: "search",
  },
  {
    className: "onboarding-layer-list",
    widget: "LayerList",
    position: "right",
    translation: "layerList",
  },
  {
    widget: "BasemapGallery",
    className: "onboarding-basemap",
    position: "right",
    translation: "basemap",
  },
  {
    widget: "Filter",
    className: "onboarding-filter",
    position: "right",
    translation: "filter",
  },
  {
    widget: "Print",
    className: "onboarding-print",
    position: "right",
    translation: "print",
  },
  {
    widget: "FeatureTable",
    className: "onboarding-export-layers",
    position: "right",
    translation: "exportLayers",
  },
  {
    widget: "LanguageSwitcher",
    className: "onboarding-language",
    position: "right",
    translation: "language",
  },
  {
    className: "onboarding-support",
    position: "right",
    translation: "support",
  },
  {
    className: "onboarding-account",
    position: "right",
    translation: "account",
  },
];

const useOnboarding = () => {
  const [onBoardingTour, setOnBoardingTour] = useState();
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation("common");
  const isSidebarOpen = useSelector((state) => state.sidebarReducer.open);
  const dispatch = useDispatch();

  useEffect(() => {
    if (config.role === ROLE_EDITOR) return;

    const getDesktopSteps = () => {
      return DESKTOP_STEPS.filter(
        (step) => !step.widget || isWidgetDisplayed(config, step.widget)
      ).map((step, index) => ({
        id: index,
        when: {
          show() {
            replaceContent({
              title: t(`onboarding.${step.translation}.title`),
              description: t(`onboarding.${step.translation}.description`),
              tour: tour,
            });
          },
        },
        attachTo: {
          element: `.${step.className}`,
          on: step.position,
        },
        floatingUIOptions: {
          middleware: [
            offset({ mainAxis: 20, crossAxis: 12 }),
            shift({ padding: 10 }),
          ],
        },
      }));
    };

    //module, share information, legend, data-table

    const getMobileSteps = () => {
      const steps = [
        {
          id: "1",
          when: {
            show() {
              replaceContent({
                title: t("onboarding.modules.title"),
                description: t("onboarding.modules.description"),
                tour: tour,
              });
            },
          },
          attachTo: {
            element: ".onboarding-modules",
            on: "bottom",
          },
          floatingUIOptions: {
            middleware: [offset({ mainAxis: 20, crossAxis: 12 })],
          },
        },
        {
          id: "2",
          when: {
            show() {
              replaceContent({
                title: t("onboarding.mobileNavigation.title"),
                description: t("onboarding.mobileNavigation.description"),
                tour: tour,
              });
            },
          },
          attachTo: {
            element: ".onboarding-navigation",
            on: "bottom",
          },
        },
      ];

      if (isWidgetDisplayed(config, "Search")) {
        steps.push({
          id: "3",
          when: {
            show() {
              replaceContent({
                title: t("onboarding.search.title"),
                description: t("onboarding.search.description"),
                tour: tour,
              });
            },
          },
          attachTo: {
            element: ".onboarding-search",
            on: "bottom",
          },
          floatingUIOptions: {
            middleware: [offset({ mainAxis: 10, crossAxis: 0 })],
          },
        });
      }

      if (isWidgetDisplayed(config, "Filter")) {
        steps.push({
          id: "4",
          when: {
            show() {
              replaceContent({
                title: t("onboarding.filterFields.title"),
                description: t("onboarding.filterFields.description"),
                tour: tour,
              });
            },
          },
          attachTo: {
            element: ".onboarding-mobile-filter",
            on: "bottom",
          },
          floatingUIOptions: {
            middleware: [offset({ mainAxis: 10, crossAxis: 0 })],
          },
        });
      }

      if (isWidgetDisplayed(config, "Report")) {
        steps.push({
          id: "5",
          when: {
            show() {
              replaceContent({
                title: t("onboarding.report.title"),
                description: t("onboarding.report.description"),
                tour: tour,
              });
            },
          },
          attachTo: {
            element: ".onboarding-report",
            on: window.isSmall ? "top" : "left",
          },
          floatingUIOptions: {
            middleware: [offset({ mainAxis: 10, crossAxis: 0 })],
          },
        });
      }

      if (isWidgetDisplayed(config, "SymbologyLegend")) {
        steps.push({
          id: "6",
          when: {
            show() {
              replaceContent({
                title: t("onboarding.legend.title"),
                description: t("onboarding.legend.description"),
                tour: tour,
              });
            },
          },
          attachTo: {
            element: ".onboarding-legend",
            on: "top",
          },
          floatingUIOptions: {
            middleware: [offset({ mainAxis: 10, crossAxis: 0 })],
          },
        });
      }

      return steps;
    };

    const replaceContent = ({ title, description, tour }) => {
      const currentStep = Shepherd.activeTour?.getCurrentStep();
      const currentStepElement = currentStep?.getElement();
      const content = currentStepElement?.querySelector(".shepherd-content");
      const progress = document.createElement("div");
      const root = createRoot(progress);
      root.render(
        <Provider store={store}>
          <OnBoarding
            total={Shepherd.activeTour?.steps.length}
            config={config}
            title={title}
            description={description}
            tour={tour}
            t={t}
          />
        </Provider>
      );
      content.innerHTML = "";
      content.append(progress);
    };

    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: "shadow-md",
        modalOverlayOpeningRadius: 8,
        showOn() {
          const element = document.querySelector(this.attachTo.element);
          return Boolean(element);
        },
      },
    });

    const isMobile = config.embed === EMBED_VARIANTS.APP || window.isSmall;

    const steps = isMobile ? getMobileSteps() : getDesktopSteps();

    tour.addSteps(steps);
    // if (!activeModule){
    //   tour.steps.splice(0,1)
    // }

    // const handleEnter = (e) => {
    //   if (e.key === "Enter") {
    //     const nextBtn = document.querySelector(
    //       "#onboarding-next-" + tour.getCurrentStep().id
    //     );
    //     if (nextBtn) {
    //       nextBtn.click();
    //     }
    //   }
    // };

    // const mapOverlay = document.querySelector(".esri-overlay-surface");
    // tour.on("start", () => {
    //   document.addEventListener("keypress", handleEnter);
    //   if (mapOverlay) {
    //     mapOverlay.style = "backdrop-filter: blur(10px);";
    //   }
    // });

    // tour.on("complete", () => {
    //   document.removeEventListener("keypress", handleEnter);
    //   if (mapOverlay) {
    //     mapOverlay.style.removeProperty("backdrop-filter");
    //   }
    // });

    setOnBoardingTour(tour);
  }, [config]);

  useEffect(() => {
    if (!onBoardingTour) return;
    const handleEnter = (e) => {
      if (e.key === "Enter") {
        const nextBtn = document.querySelector(
          "#onboarding-next-" + onBoardingTour.getCurrentStep().id
        );
        if (nextBtn) {
          nextBtn.click();
        }
      }
    };

    const onStart = () => {
      const mapOverlay = document.querySelector(".esri-overlay-surface");
      document.addEventListener("keypress", handleEnter);
      dispatch(setOnboardingActive(true));

      if (mapOverlay) {
        mapOverlay.style = "backdrop-filter: blur(10px);";
      }
    };

    const onFinish = () => {
      const mapOverlay = document.querySelector(".esri-overlay-surface");
      document.removeEventListener("keypress", handleEnter);
      dispatch(setOnboardingActive(false));
      if (mapOverlay) {
        mapOverlay.style.removeProperty("backdrop-filter");
      }
    };

    onBoardingTour.on("start", onStart);
    onBoardingTour.on("complete", onFinish);
    onBoardingTour.on("cancel", onFinish);
    onBoardingTour.on("hide", onFinish);

    return () => {
      const mapOverlay = document.querySelector(".esri-overlay-surface");
      onBoardingTour.off("start", onStart);
      onBoardingTour.off("complete", onFinish);
      onBoardingTour.off("cancel", onFinish);
      onBoardingTour.off("hide", onFinish);
      document.removeEventListener("keypress", handleEnter);
      if (mapOverlay) {
        mapOverlay.style.removeProperty("backdrop-filter");
      }
    };
  }, [onBoardingTour, isSidebarOpen]);

  return {
    onBoardingTour,
    setOnBoardingTour,
  };
};

export default useOnboarding;
